import React from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import SSNInput from "@/components/SSNInput";
import PhoneInput from "@/components/PhoneInput";

export function FieldRenderer({ field, value, onChange, disabled, error, errorId, maskedLast4 }) {
    // Inline-error a11y wiring — when the parent (SubmissionFormPage) has
    // a per-field error from the backend's structured 400 response, it
    // passes `error` (string) + `errorId` (the id of the <p role="alert">
    // it renders below this input). We flip aria-invalid on the matching
    // <input> and point aria-describedby at the error node so screen
    // readers announce the message in context. Visual styling stays
    // light — just a red ring — to match the existing form's vibe.
    const invalid = !!error;
    const invalidClass = invalid
        ? "border-red-400 focus-visible:ring-red-500"
        : "";

    if (field.type === "composite") {
        const sub = value && typeof value === "object" ? value : {};
        return (
            <fieldset
                className="rounded-md border border-slate-200 bg-white p-4 space-y-3"
                data-testid={`composite-${field.key}`}
                aria-invalid={invalid || undefined}
                aria-describedby={errorId}
            >
                <legend className="px-1 text-sm font-medium text-slate-900">{field.label}</legend>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {field.subfields.map((sf) => (
                        <div key={sf.key} className="space-y-1.5">
                            <Label htmlFor={`${field.key}_${sf.key}`} className="text-xs text-slate-600">
                                {sf.label}
                            </Label>
                            {sf.key === "phone" ? (
                                <PhoneInput
                                    data-testid={`field-${field.key}-${sf.key}`}
                                    value={sub[sf.key] || ""}
                                    onChange={(v) => onChange({ ...sub, [sf.key]: v })}
                                    disabled={disabled}
                                />
                            ) : (
                                <Input
                                    id={`${field.key}_${sf.key}`}
                                    data-testid={`field-${field.key}-${sf.key}`}
                                    value={sub[sf.key] || ""}
                                    onChange={(e) => onChange({ ...sub, [sf.key]: e.target.value })}
                                    disabled={disabled}
                                    placeholder={sf.label}
                                    aria-invalid={invalid || undefined}
                                />
                            )}
                        </div>
                    ))}
                </div>
            </fieldset>
        );
    }

    if (field.type === "usdot") {
        return (
            <div className="space-y-1">
                <Input
                    id={field.key}
                    data-testid={`field-${field.key}`}
                    type="text"
                    inputMode="numeric"
                    autoComplete="off"
                    value={value || ""}
                    onChange={(e) => onChange(e.target.value.replace(/\D/g, "").slice(0, 8))}
                    disabled={disabled}
                    placeholder="e.g. 1234567"
                    className={invalidClass}
                    aria-invalid={invalid || undefined}
                    aria-describedby={errorId}
                />
                <p className="text-xs text-slate-500" data-testid={`field-${field.key}-help`}>
                    5–8 digit USDOT number issued by FMCSA
                </p>
            </div>
        );
    }

    const inputType =
        field.type === "email"
            ? "email"
            : field.type === "phone"
              ? "tel"
              : field.type === "date"
                ? "date"
                : "text";

    const placeholder =
        field.type === "ein"
            ? "XX-XXXXXXX"
            : field.type === "ssn"
              ? "XXX-XX-XXXX"
              : "";

    // Task 11 — SSN gets the dedicated input (live-formats, blur-masks).
    // ``maskedLast4`` is plumbed in by parent pages for existing-record
    // edit flows (e.g. AdminCustomerDetailPage reads it from
    // ``customer.guarantor_ssn_last4``) so the field renders the
    // canonical ``•••-••-LLLL`` mask when no edit is in progress.
    if (field.type === "ssn") {
        return (
            <SSNInput
                data-testid={`field-${field.key}`}
                value={value || ""}
                onChange={onChange}
                disabled={disabled}
                placeholder={placeholder}
                maskedLast4={maskedLast4}
            />
        );
    }

    // Task 11b — phone goes through PhoneInput for live ``(XXX) XXX-XXXX``
    // formatting + NANP validation. Storage is bare 10 digits.
    if (field.type === "phone") {
        return (
            <PhoneInput
                data-testid={`field-${field.key}`}
                value={value || ""}
                onChange={onChange}
                disabled={disabled}
                required={!!field.required}
            />
        );
    }

    return (
        <Input
            id={field.key}
            data-testid={`field-${field.key}`}
            type={inputType}
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
            placeholder={placeholder}
            className={invalidClass}
            aria-invalid={invalid || undefined}
            aria-describedby={errorId}
        />
    );
}
